import { GetIdeaResponse } from '@orbit/api-types'
import { usePortalDropdownPosition } from '@hooks'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
	IdeaCategory,
	IdeaDetails,
	IdeaDropdown,
	IdeaDropdownItem,
	IdeaInput,
	IdeaLabel,
	IdeaName,
	IdeaSelectContainer,
	IdeaSelectWrapper,
	SearchIcon,
} from './IdeaSelect.styles'
import { EmptyNote } from '@components/EmptyState'
import { TestIds } from '@/testIds'

const EVENT_CATEGORY_LABELS: Record<string, string> = {
	FoodAndDrink: 'Food & Drink',
	SeeAndDo: 'See & Do',
	Nature: 'Nature',
	Shopping: 'Shopping',
	Accommodation: 'Accommodation',
	Transportation: 'Transportation',
	Event: 'Event',
	Other: 'Other',
}

interface IdeaSelectProps {
	ideas: GetIdeaResponse[]
	value: number | null
	onChange: (ideaId: number) => void
	label?: string
	htmlFor?: string
}

const IdeaSelect: React.FC<IdeaSelectProps> = ({ ideas, value, onChange, label, htmlFor }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const containerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const dropdownCoords = usePortalDropdownPosition(containerRef, isOpen)

	const selectedIdea = ideas.find(b => b.id === value)

	// Filter ideas based on search query
	const filteredIdeas = ideas.filter(idea => {
		const query = searchQuery.toLowerCase()
		return (
			idea.name.toLowerCase().includes(query) ||
			(idea.description && idea.description.toLowerCase().includes(query)) ||
			(idea.location?.primaryText && idea.location.primaryText.toLowerCase().includes(query)) ||
			EVENT_CATEGORY_LABELS[idea.category].toLowerCase().includes(query)
		)
	})

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const insideContainer = containerRef.current?.contains(event.target as Node)
			const insideDropdown = dropdownRef.current?.contains(event.target as Node)
			if (!insideContainer && !insideDropdown) {
				setIsOpen(false)
				setSearchQuery('')
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	const handleSelect = (idea: GetIdeaResponse) => {
		onChange(idea.id)
		setIsOpen(false)
		setSearchQuery('')
	}

	const handleInputFocus = () => {
		setIsOpen(true)
	}

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value)
		setIsOpen(true)
	}

	const getDisplayValue = () => {
		if (isOpen) {
			return searchQuery
		}
		if (selectedIdea) {
			return selectedIdea.name
		}
		return ''
	}

	return (
		<IdeaSelectContainer>
			{label && (
				<IdeaLabel htmlFor={htmlFor}>
					{label}
				</IdeaLabel>
			)}
			<IdeaSelectWrapper ref={containerRef}>
				<IdeaInput
					ref={inputRef}
					data-testid={TestIds.idea.selectField}
					type="text"
					value={getDisplayValue()}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					placeholder="Search ideas..."
					readOnly={!isOpen}
				/>
				<SearchIcon>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
							stroke="currentColor"
							strokeWidth="1.333"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</SearchIcon>
				{isOpen &&
					dropdownCoords &&
					createPortal(
						<IdeaDropdown
							ref={dropdownRef}
							$top={dropdownCoords.top}
							$left={dropdownCoords.left}
							$width={dropdownCoords.width}
						>
							{filteredIdeas.length > 0 ? (
								filteredIdeas.map(idea => (
									<IdeaDropdownItem
										key={idea.id}
										data-testid={TestIds.idea.cell(idea.name)}
										onClick={() => handleSelect(idea)}
										$isSelected={idea.id === value}
									>
										<IdeaName>{idea.name}</IdeaName>
										<IdeaDetails>
											<IdeaCategory>{EVENT_CATEGORY_LABELS[idea.category]}</IdeaCategory>
											{idea.location?.primaryText && <span>• {idea.location.primaryText}</span>}
										</IdeaDetails>
									</IdeaDropdownItem>
								))
							) : (
								<EmptyNote>No ideas found</EmptyNote>
							)}
						</IdeaDropdown>,
						document.body,
					)}
			</IdeaSelectWrapper>
		</IdeaSelectContainer>
	)
}

export default IdeaSelect

